import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';

import { Key, Plus, Edit, ChevronDown, ChevronRight, CheckCircle, Eye } from 'lucide-react';

import { PERMISSION_CATEGORIES } from './constants';
import { MenusSelector } from './MenusSelector';

export default function GestionRolesModals({ ctx }) {
  const { deselectAllPermissions, expandedCategories, getRoleConfig, groupedPermissions, handleCreateRole, handleUpdateRole, isSuperAdmin, openEditModal, _permissions, roleForm, selectAllInCategory, selectAllPermissions, selectedRole, showPermissionsModal, showRoleModal, toggleCategory, togglePermission, setRoleForm, setShowPermissionsModal, setShowRoleModal } = ctx;
  return (
    <>
      {/* Modal création/édition */}
      <Dialog open={showRoleModal} onOpenChange={setShowRoleModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedRole ? (
                <>
                  <Edit className="h-5 w-5 text-purple-600" />
                  Modifier le rôle &quot;{selectedRole.display_name}&quot;
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5 text-purple-600" />
                  Créer un nouveau rôle
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-6 py-4">
            {/* Informations de base */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Informations générales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Identifiant technique *</Label>
                    <Input
                      value={roleForm.name}
                      onChange={(e) => setRoleForm({...roleForm, name: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                      placeholder="mon_role"
                      disabled={!!selectedRole}
                      className="font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">Utilisé dans le code (sans espaces)</p>
                  </div>
                  <div>
                    <Label>Nom affiché *</Label>
                    <Input
                      value={roleForm.display_name}
                      onChange={(e) => setRoleForm({...roleForm, display_name: e.target.value})}
                      placeholder="Mon Rôle"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-3">
                    <Label>Description</Label>
                    <Textarea
                      value={roleForm.description}
                      onChange={(e) => setRoleForm({...roleForm, description: e.target.value})}
                      placeholder="Description du rôle et de ses responsabilités..."
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label>Couleur</Label>
                    <div className="flex gap-2 items-center mt-1">
                      <Input
                        type="color"
                        value={roleForm.color}
                        onChange={(e) => setRoleForm({...roleForm, color: e.target.value})}
                        className="w-14 h-10 p-1 cursor-pointer"
                      />
                      <Input
                        value={roleForm.color}
                        onChange={(e) => setRoleForm({...roleForm, color: e.target.value})}
                        className="flex-1 font-mono text-sm"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {!selectedRole?.is_system && (
              <MenusSelector menus={roleForm.menus} onChange={(menus) => setRoleForm({ ...roleForm, menus })} />
            )}

            {/* Permissions */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    Permissions
                    <Badge className="bg-purple-800 text-white ml-2 font-medium">
                      {roleForm.permissions.length} sélectionnées
                    </Badge>
                  </CardTitle>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllPermissions}>
                      Tout sélectionner
                    </Button>
                    <Button size="sm" variant="outline" onClick={deselectAllPermissions}>
                      Tout désélectionner
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg divide-y max-h-[350px] overflow-y-auto">
                  {Object.entries(groupedPermissions).map(([category, perms]) => {
                    const catConfig = PERMISSION_CATEGORIES[category] || { icon: Key, label: category, color: 'text-gray-600' };
                    const CategoryIcon = catConfig.icon;
                    const categoryPerms = perms.map(p => p.key);
                    const selectedCount = categoryPerms.filter(p => roleForm.permissions.includes(p)).length;
                    const isExpanded = expandedCategories[category];
                    const allSelected = selectedCount === perms.length;
                    
                    return (
                      <div key={category}>
                        <div 
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => toggleCategory(category)}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? 
                              <ChevronDown className="h-4 w-4 text-gray-400" /> : 
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            }
                            <CategoryIcon className={`h-5 w-5 ${catConfig.color}`} />
                            <span className="font-medium">{catConfig.label}</span>
                            <Badge 
                              className={`${allSelected ? 'bg-green-700' : selectedCount > 0 ? 'bg-amber-600' : 'bg-gray-500'} text-white`}
                            >
                              {selectedCount}/{perms.length}
                            </Badge>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectAllInCategory(category);
                            }}
                            className="text-xs"
                          >
                            {allSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
                          </Button>
                        </div>
                        
                        {isExpanded && (
                          <div className="px-4 pb-3 grid grid-cols-1 md:grid-cols-2 gap-2 bg-gray-50">
                            {perms.map((perm) => {
                              const isSelected = roleForm.permissions.includes(perm.key);
                              return (
                                <div 
                                  key={perm.key}
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                    isSelected ? 'bg-green-100 border border-green-300' : 'bg-white border border-gray-200 hover:border-gray-300'
                                  }`}
                                  onClick={() => togglePermission(perm.key)}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => togglePermission(perm.key)}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{perm.key}</div>
                                    <div className="text-xs text-gray-500 truncate">{perm.description}</div>
                                  </div>
                                  {isSelected && (
                                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowRoleModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={selectedRole ? handleUpdateRole : handleCreateRole}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {selectedRole ? 'Mettre à jour' : 'Créer le rôle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal visualisation des permissions */}
      <Dialog open={showPermissionsModal} onOpenChange={setShowPermissionsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Permissions du rôle &quot;{selectedRole?.display_name}&quot;
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {selectedRole && (
              <div className="space-y-4">
                {/* Info rôle */}
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <div 
                    className="p-3 rounded-lg"
                    style={{ 
                      backgroundColor: `${getRoleConfig(selectedRole).color}20`,
                      color: getRoleConfig(selectedRole).color 
                    }}
                  >
                    {React.createElement(getRoleConfig(selectedRole).icon, { className: "h-6 w-6" })}
                  </div>
                  <div>
                    <h3 className="font-semibold">{selectedRole.display_name}</h3>
                    <p className="text-sm text-gray-500">{selectedRole.description || 'Aucune description'}</p>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-2xl font-bold text-purple-600">{selectedRole.permissions?.length || 0}</div>
                    <div className="text-xs text-gray-500">permissions</div>
                  </div>
                </div>
                
                {/* Liste des permissions par catégorie */}
                <div className="space-y-3">
                  {Object.entries(groupedPermissions).map(([category, perms]) => {
                    const catConfig = PERMISSION_CATEGORIES[category] || { icon: Key, label: category, color: 'text-gray-600' };
                    const CategoryIcon = catConfig.icon;
                    const selectedPerms = perms.filter(p => selectedRole.permissions?.includes(p.key));
                    
                    if (selectedPerms.length === 0) return null;
                    
                    return (
                      <div key={category} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center gap-2 p-3 bg-gray-50 border-b">
                          <CategoryIcon className={`h-4 w-4 ${catConfig.color}`} />
                          <span className="font-medium">{catConfig.label}</span>
                          <Badge className="bg-green-800 text-white ml-auto font-medium">
                            {selectedPerms.length}
                          </Badge>
                        </div>
                        <div className="p-3 grid grid-cols-2 gap-2">
                          {selectedPerms.map(perm => (
                            <div key={perm.key} className="flex items-center gap-2 text-sm">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <span>{perm.key}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setShowPermissionsModal(false)}>
              Fermer
            </Button>
            {isSuperAdmin && (
              <Button 
                onClick={() => {
                  setShowPermissionsModal(false);
                  openEditModal(selectedRole);
                }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Edit className="h-4 w-4 mr-1" />
                Modifier
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
